// Admin save and the translation worker MUST compute byte-identical keys via
// `toSegmentKey`, or every override silently orphans.
//
// A segment is one SENTENCE of a text block, with inline formatting (bold,
// italic, link, underline) serialised as tags so the whole sentence is
// translated as a unit (see translationSegmentExtract). Colour is presentational
// and is NOT part of a segment or its key.
//
// Keys are content-hashed on the sentence's PLAIN text (tags stripped) plus an
// occurrence index, so re-nesting or reordering the document structure does not
// re-key unchanged text, and a formatting-only edit keeps the key (the tagged
// source drift check re-translates it when needed). Conversation keys keep the
// node id as an anchor so they survive node reordering; page keys are
// position-independent within the document.

export type SegmentLocator =
  | { kind: 'CONVERSATION_SENTENCE'; nodeId: string; sourceHash: string; occurrenceIndex: number }
  | { kind: 'CONVERSATION_OPTION'; nodeId: string; optionIndex: number }
  | { kind: 'PAGE_SENTENCE'; sourceHash: string; occurrenceIndex: number };

// FNV-1a 64-bit as padded hex. 64-bit (not 32) so within-document key collisions
// are negligible even for large learner guides (thousands of sentences).
export function hashSourceText(text: string): string {
  let hash = 0xcbf29ce484222325n;
  const prime = 0x100000001b3n;
  const mask = 0xffffffffffffffffn;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= BigInt(text.charCodeAt(index));
    hash = (hash * prime) & mask;
  }
  return hash.toString(16).padStart(16, '0');
}

export function toSegmentKey(locator: SegmentLocator): string {
  switch (locator.kind) {
    case 'CONVERSATION_SENTENCE':
      return `node:${locator.nodeId}|${locator.sourceHash}#${locator.occurrenceIndex}`;
    case 'CONVERSATION_OPTION':
      return `node:${locator.nodeId}|opt:${locator.optionIndex}`;
    case 'PAGE_SENTENCE':
      return `page:${locator.sourceHash}#${locator.occurrenceIndex}`;
  }
}
